// "Directions" to an asset or a project (2026-10-08): the user asked for a
// way to get crews to the right spot without "wandering around tapping on
// the radar/camera".
//
// - Directions: a plain link that opens the phone's own maps app (Apple Maps
//   on iOS, Google Maps' universal URL elsewhere) with the destination's
//   coordinates. Links only - no embedded Google services (the Street View
//   embed was removed for licensing reasons).
// - Guide me: the field app's in-app arrow + countdown for the last stretch
//   on foot (field/guide.ts), reached here with a "wcc-field-guide" event.
//
// The destination: an asset's own location (a pipe: its nearest point to the
// user when the position is known, else its middle); a project: the nearest
// point of its pipes to the user, else the middle of its first pipe.
import { fieldApi } from "./field/fieldApi";
import type { LonLat, TravelMode } from "./field/geoMath";
import { nearestTarget, preferredDirectionsLink } from "./field/geoMath";
import { el } from "./legend";
import { getState } from "./state";
import { showToast } from "./toast";
import { workspaceChoices } from "./workspaces";

export type DirectionsTarget = { assetId: string; projectId?: undefined } | { projectId: string; assetId?: undefined };

export const FIELD_GUIDE_EVENT = "wcc-field-guide";
export interface FieldGuideDetail {
  asset_id?: string;
  project_id?: string;
}

/** The user's position if the browser gives it within `ms` (never prompts
 * twice: a denied permission just answers null). */
export function quickPosition(ms = 4000): Promise<{ lat: number; lon: number; accuracy: number } | null> {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const t = window.setTimeout(() => resolve(null), ms);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        window.clearTimeout(t);
        resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy });
      },
      () => {
        window.clearTimeout(t);
        resolve(null);
      },
      { enableHighAccuracy: false, maximumAge: 120000, timeout: ms },
    );
  });
}

function middle(path: LonLat[]): LonLat {
  return path[Math.floor(path.length / 2)];
}

/** The destination point for a target (throws when it can't be located). */
export async function destination(t: DirectionsTarget, at: { lat: number; lon: number } | null): Promise<{ lat: number; lon: number; label: string }> {
  if (t.assetId) {
    const a = await fieldApi.locate(t.assetId);
    if (a.path && a.path.length) {
      const near = at ? nearestTarget(at.lat, at.lon, [{ id: a.asset_id, path: a.path }]) : null;
      const p = near ? [near.lon, near.lat] : middle(a.path);
      return { lon: p[0], lat: p[1], label: a.label || a.asset_id };
    }
    if (typeof a.lat === "number" && typeof a.lon === "number") return { lat: a.lat, lon: a.lon, label: a.label || a.asset_id };
    throw new Error("no location");
  }
  const site = await fieldApi.projectSite(t.projectId as string);
  const pipes = site.pipes.filter((p) => p.path?.length);
  if (!pipes.length) throw new Error("no pipes");
  const near = at ? nearestTarget(at.lat, at.lon, pipes.map((p) => ({ id: p.asset_id, path: p.path }))) : null;
  const p = near ? [near.lon, near.lat] : middle(pipes[0].path);
  return { lon: p[0], lat: p[1], label: site.project.name };
}

/** Open directions in the device's maps app. The window is opened at once
 * (inside the click, so it isn't blocked as a pop-up) and pointed at the
 * maps link once the destination is known. */
export async function openDirections(t: DirectionsTarget, mode: TravelMode): Promise<void> {
  const win = window.open("about:blank", "_blank");
  try {
    const at = await quickPosition();
    const d = await destination(t, at);
    const url = preferredDirectionsLink(navigator.userAgent, d.lat, d.lon, mode, d.label);
    if (win && !win.closed) win.location.href = url;
    else window.location.href = url;
  } catch {
    win?.close();
    showToast(t.assetId ? "Couldn't find where that asset is (or you can't see it)." : "Couldn't load that project's pipes.");
  }
}

/** True when this user has a field workspace (the in-app "Guide me"). */
export function canGuide(): boolean {
  return workspaceChoices(getState().currentUser).some((w) => w === "operations" || w === "construction");
}

/** "Directions: Walk · Drive · Guide me" - small buttons for panels, search
 * results and My work rows. Clicks don't bubble (rows are clickable). */
export function directionsControls(t: DirectionsTarget, opts: { compact?: boolean } = {}): HTMLElement {
  const wrap = el("span", { className: `directions-controls${opts.compact ? " compact" : ""}` });
  const btn = (label: string, title: string, run: () => void) => {
    const b = el("button", { className: "link-button directions-btn", type: "button", title }, label);
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      run();
    });
    return b;
  };
  if (!opts.compact) wrap.append(el("span", { className: "caption" }, "Directions: "));
  wrap.append(
    btn(opts.compact ? "Directions" : "Walk", "Open walking directions in your maps app", () => void openDirections(t, "walking")),
  );
  if (!opts.compact) wrap.append(btn("Drive", "Open driving directions in your maps app", () => void openDirections(t, "driving")));
  if (canGuide()) {
    wrap.append(btn("Guide me", "The field app's arrow and distance countdown, for the last stretch on foot", () => {
      const detail: FieldGuideDetail = t.assetId ? { asset_id: t.assetId } : { project_id: t.projectId };
      document.dispatchEvent(new CustomEvent<FieldGuideDetail>(FIELD_GUIDE_EVENT, { detail }));
    }));
  }
  return wrap;
}
